'use client';

import React, { useMemo } from 'react';
import { BetaFlag } from 'src/components';
import {
  ProfileTreatingVolumeChart,
  ProfileTreatingVolumeItem,
} from './components';
import { CardLoadingIndicator } from '../Card';
import {
  faFiles,
  faUserDoctorHairMullet,
} from '@fortawesome/pro-regular-svg-icons';
import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { useGetCodeSetsVolumeQuery } from 'src/api/redux';
import { useProfile } from 'src/context/providers/ProfileProvider';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import {
  PROFILE_VOLUME_MIN_YEAR,
  PROFILE_VOLUME_MAX_YEAR,
} from 'src/components/constants';
interface ProfileTreatingVolumeProps {
  location?: 'hcp' | 'center' | 'clinic' | 'system' | 'site_of_care';
}
const ProfileTreatingVolume: React.FC<ProfileTreatingVolumeProps> = ({
  location = 'hcp',
}) => {
  const { entityDetails } = useProfile();
  const { selectedFilterSet, isAllCodeSets } = useProfileSelections(location);

  const {
    data: volumeDataResponse,
    isLoading,
    isFetching,
  } = useGetCodeSetsVolumeQuery(
    {
      entityId: entityDetails?.id || '',
      params: {
        code_set_ids: selectedFilterSet?.id || [],
        all_code_sets: isAllCodeSets,
        min_year: PROFILE_VOLUME_MIN_YEAR,
        max_year: PROFILE_VOLUME_MAX_YEAR,
      },
    },
    {
      skip: !entityDetails?.id,
    }
  );

  const isDataLoading = isLoading || isFetching;

  const volumeTotalItems = useMemo(() => {
    const {
      total_claims = '0',
      total_patients = '0',
      total_providers = '0',
    } = volumeDataResponse ?? {};

    return [
      {
        title: 'Total Claims',
        value: handleNumericValue(total_claims),
        icon: faFiles,
      },
      {
        title: 'Patients',
        value: handleNumericValue(total_patients),
        icon: faPeopleGroup,
        tooltip:
          'Patients can be double counted where multiple billing codes are used in same visit',
      },
      {
        title: 'Providers',
        value: handleNumericValue(total_providers),
        icon: faUserDoctorHairMullet,
        show: !total_providers || Number(total_providers) > 0,
      },
    ].filter((item) => item.show !== false);
  }, [volumeDataResponse]);

  return (
    <div
      className="@container relative pl-6 py-5 w-full h-128 bg-white flex flex-col gap-5 rounded-lg border border-neutral-200 shadow-sm"
      data-testid="profile-treating-volume"
      role="region"
      aria-label="Treating Volume"
    >
      {isDataLoading && <CardLoadingIndicator />}
      <div className="self-stretch flex flex-col pr-6">
        <div className="flex items-center gap-2">
          <h3
            className="text-primary text-base font-bold leading-6"
            id="treating-volume-title"
          >
            Treating Volume
          </h3>
          <BetaFlag size="small" />
        </div>
        <p
          className="text-secondary text-sm leading-5 line-clamp-1"
          id="treating-volume-description"
        >
          Volumes for your Code Sets from &apos;25.
        </p>
      </div>
      {/* Card Row */}
      <div className="relative flex-1 min-h-0">
        <div className="flex flex-col gap-5 overflow-y-auto h-full pr-6 pb-6">
          <div className="grid grid-cols-1 @sm:grid-cols-2 @2xl:grid-cols-3 gap-4">
            {volumeTotalItems.map((item) => (
              <ProfileTreatingVolumeItem
                key={item.title}
                title={item.title}
                value={item.value?.toString()}
                icon={item.icon}
                tooltip={item.tooltip}
              />
            ))}
          </div>
          {/* Chart Content */}
          <ProfileTreatingVolumeChart location={location} />
        </div>
        {/* Shadow overlay */}
        <div className="absolute bottom-0 left-0 right-6 h-10 pointer-events-none shadow-[inset_0_-10px_10px_-10px_rgba(0,0,0,0.1)]" />
      </div>
      <div className="self-stretch justify-start text-secondary text-xs font-normal font-['Inter'] leading-4 line-clamp-1 pr-6">
        <sup>**</sup>patients can be double counted where multiple billing codes
        are used in same visit
      </div>
    </div>
  );
};

export default ProfileTreatingVolume;
